// RawMaterialPriceUpdatePage.jsx
// Sets the item master price of raw materials to the rate they were last bought at.
// Raw materials are the items used in a production definition.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
});

const money = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "");

// The rate a row would be set to: the last purchase rate (before or including tax), or the
// purchase rate typed on the item master when the item has never been bought.
const purchaseRateOf = (row, inclTax) => {
  const last = inclTax ? row.lastPurchaseRateInclTax : row.lastPurchaseRate;
  if (last !== null && last !== undefined) return Number(last);
  if (row.itemMasterPurchaseRate) return Number(row.itemMasterPurchaseRate);
  return null;
};

const sameMoney = (a, b) => a !== null && b !== null && Math.abs(Number(a) - Number(b)) < 0.005;

async function errorText(res) {
  try {
    const data = await res.json();
    return data?.errorMessage || data?.message || `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

const RawMaterialPriceUpdatePage = () => {
  const tenancyId = localStorage.getItem("tenancyId");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [inclTax, setInclTax] = useState(false);
  const [query, setQuery] = useState("");
  const [onlyDifferent, setOnlyDifferent] = useState(true);
  const [edits, setEdits] = useState({});     // id -> typed price (string)
  const [selected, setSelected] = useState({}); // id -> true
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/${tenancyId}/pricing/raw-material-purchase-rates`, { headers: authHeaders() });
      if (!res.ok) throw new Error(await errorText(res));
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setEdits({});
      setSelected({});
    } catch (e) {
      setError(e.message || "Could not load raw materials.");
    } finally {
      setLoading(false);
    }
  }, [tenancyId]);

  useEffect(() => { load(); }, [load]);

  const newPriceOf = useCallback(
    (row) => {
      if (edits[row.id] !== undefined) return edits[row.id] === "" ? null : Number(edits[row.id]);
      return purchaseRateOf(row, inclTax);
    },
    [edits, inclTax]
  );

  const isValidPrice = (p) => p !== null && Number.isFinite(p) && p >= 0;
  const changes = (row) => {
    const p = newPriceOf(row);
    return isValidPrice(p) && !sameMoney(p, row.currentPrice);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !`${r.itemName || ""} ${r.itemCode || ""}`.toLowerCase().includes(q)) return false;
      if (onlyDifferent && !changes(r)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, onlyDifferent, newPriceOf]);

  const selectable = visible.filter(changes);
  const chosen = rows.filter((r) => selected[r.id] && changes(r));
  const allSelected = selectable.length > 0 && selectable.every((r) => selected[r.id]);

  const toggleAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      selectable.forEach((r) => { next[r.id] = !allSelected; });
      return next;
    });
  };

  const save = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`/api/${tenancyId}/pricing/raw-material-prices`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          items: chosen.map((r) => ({ id: r.id, standardPrice: Math.round(newPriceOf(r) * 100) / 100 })),
        }),
      });
      if (!res.ok) throw new Error(await errorText(res));
      const data = await res.json();
      setSuccess(`Updated the price of ${data.updatedItems} item${data.updatedItems === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      setError(e.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const withBranchPrices = chosen.filter((r) => r.hasBranchPrices).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Raw Material Price Update</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sets the item master price of raw materials to their last purchase rate. Raw materials are
          the items used in a production definition. You can change any new price before saving.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: "center" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Purchase rate</InputLabel>
            <Select label="Purchase rate" value={inclTax ? "incl" : "ex"} onChange={(e) => setInclTax(e.target.value === "incl")}>
              <MenuItem value="ex">Before tax</MenuItem>
              <MenuItem value="incl">Including tax</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Search items" value={query} onChange={(e) => setQuery(e.target.value)} />
          <FormControlLabel
            control={<Switch checked={onlyDifferent} onChange={(e) => setOnlyDifferent(e.target.checked)} />}
            label="Only items whose price will change"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving || chosen.length === 0}
            onClick={() => setConfirmOpen(true)}
          >
            Update {chosen.length} price{chosen.length === 1 ? "" : "s"}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer sx={{ maxHeight: "65vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && selectable.some((r) => selected[r.id])}
                      disabled={selectable.length === 0}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Current Price</TableCell>
                  <TableCell align="right">Last Purchase Rate</TableCell>
                  <TableCell>Last Purchase</TableCell>
                  <TableCell align="right" sx={{ minWidth: 130 }}>New Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      {rows.length === 0
                        ? "No raw materials found. Items appear here once they are used in a production definition."
                        : "No items match."}
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((r) => {
                  const rate = purchaseRateOf(r, inclTax);
                  const fromLast = (inclTax ? r.lastPurchaseRateInclTax : r.lastPurchaseRate) != null;
                  const p = newPriceOf(r);
                  const canSelect = changes(r);
                  return (
                    <TableRow key={r.id} hover selected={!!selected[r.id] && canSelect}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selected[r.id] && canSelect}
                          disabled={!canSelect}
                          onChange={(e) => setSelected((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                        />
                      </TableCell>
                      <TableCell>
                        {r.itemName}
                        {r.itemCode ? <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{r.itemCode}</Typography> : null}
                        {r.hasBranchPrices && (
                          <Tooltip title="Some branches have their own price for this item. Branch Price overrides are not changed.">
                            <Chip size="small" label="Branch prices" sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{r.unit}</TableCell>
                      <TableCell align="right">{money(r.currentPrice)}</TableCell>
                      <TableCell align="right">
                        {rate === null ? "-" : money(rate)}
                        {rate !== null && !fromLast && (
                          <Typography variant="caption" color="text.secondary" display="block">item master</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.lastPurchaseDate ? (
                          <>
                            {fmtDate(r.lastPurchaseDate)}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[r.lastPurchaseVoucher, r.lastPurchaseSupplier].filter(Boolean).join(" · ")}
                            </Typography>
                          </>
                        ) : "No purchase yet"}
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                          value={edits[r.id] !== undefined ? edits[r.id] : p === null ? "" : money(p)}
                          error={p !== null && !isValidPrice(p)}
                          onChange={(e) => setEdits((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Update {chosen.length} price{chosen.length === 1 ? "" : "s"}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The item master price of the selected raw materials will be set to the New Price shown.
            Running POS terminals will reload their item list.
            {withBranchPrices > 0 &&
              ` ${withBranchPrices} of these item${withBranchPrices === 1 ? " has" : "s have"} branch prices, which stay as they are.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RawMaterialPriceUpdatePage;
